import type { PostHogConfig } from 'posthog-js'
import { dropIgnoredExceptions } from './error-tracking'

/**
 * PostHog EU Cloud configuration (DO-13470).
 *
 * Data is only ever sent from production builds. Every other release stage stays silent unless a
 * developer explicitly opts in through the local dev override (see `dev-override.ts`). There is no
 * staging PostHog instance anymore.
 */

export const POSTHOG_EU_HOST = 'https://eu.i.posthog.com'

/**
 * Project tokens of the two PostHog EU Cloud projects. One project per product. Project tokens are
 * public client-side keys (like the Pendo keys); an empty token disables PostHog completely.
 */
export const POSTHOG_PROJECT_TOKENS: Record<typeof ENV.product, string> = {
  // PRODUCTION PROJECT TOKENS (see DO-13470)
  orgamax: 'phc_ChaaJuvtSPokTGUdExuL4EwKFLtpC7cEAJ8g3ZFL9pbi',
  meinbuero: 'phc_rKKDo4KkWDohE9aQQSueFfq3A35u2GaEhnTs2BHzgMoK',

  // // DEVELOPMENT PROJECT TOKENS (see DO-13470)
  // orgamax: 'phc_p4TxLYoP3aWv2JBJVJfn4PoKzxSg2wMPywqEyasa2eN3',
  // meinbuero: 'phc_vB7YyEjhqsAJ7HNyoYU2eB8a4PWmuxc3bfgnz3rQJKtA',

}

/**
 * Name of the PostHog service in the Usercentrics consent management platform (the Usercentrics
 * template is called "PostHog.com"). The name must match the service name in the Usercentrics
 * admin console for BOTH product configurations (MeinBüro `wo5ytHHt`, orgaMAX `FDavgBK4`); the
 * comparison ignores case. Consent for this service is the only gate that loads the SDK.
 */
export const USERCENTRICS_POSTHOG_SERVICE_NAME = 'PostHog.com'

/**
 * Session replay is prepared but switched off (decision by DSB/ISB, see ticket comment). Flipping
 * this constant to `true` enables recording; before doing so the masking rules in
 * `SESSION_RECORDING_OPTIONS` MUST be implemented, since recordings capture the DOM.
 */
const SESSION_REPLAY_ENABLED = false

/**
 * Masking / exclusion rules for session replay. Intentionally empty while replay is disabled.
 *
 * TODO (when enabling SESSION_REPLAY_ENABLED): at least
 *   - maskAllInputs: true
 *   - maskTextSelector: selectors for names, addresses, bank data, amounts, document contents
 *   - blockSelector: PDF viewers, e-mail bodies, chat sections
 */
const SESSION_RECORDING_OPTIONS: PostHogConfig['session_recording'] = {}

/**
 * Prefix used for distinct ids and tenant ids, identical to the Pendo prefix so users can be
 * correlated between both tools. Non-production stages get a suffix so dev override data never
 * mixes with real customers even if someone points the override at a real project.
 */
export const getIdPrefix = (product: typeof ENV.product, releaseStage: typeof ENV.releaseStage): string => {
  const base = product === 'orgamax' ? 'OM' : 'MB'
  switch (releaseStage) {
    case 'production':
      return base
    case 'prelive':
      return `${base}-PRELIVE`
    case 'local':
      return `${base}-LOCAL`
    default:
      return `${base}-DEV`
  }
}

/**
 * Error tracking. Uncaught exceptions and unhandled promise rejections are captured as
 * `$exception` events. `capture_console_errors` stays OFF on purpose: the app logs almost every
 * handled error (expected 4xx responses, validation errors, ...) via `console.error`, which turned
 * every one of them into a billed exception and buried the real bugs. Caught errors worth reporting
 * go through `capturePostHogException`, errors the user sees through the "Toast shown" event
 * (see `error-tracking.ts`).
 */
export const EXCEPTION_CAPTURE_OPTIONS: PostHogConfig['capture_exceptions'] = {
  capture_unhandled_errors: true,
  capture_unhandled_rejections: true,
  capture_console_errors: false,
}

/**
 * SDK options. Autocapture is off on purpose: only explicit `capture()` calls and SPA pageviews
 * are sent, so no button texts, form values or DOM attributes leave the browser.
 */
export const buildPostHogOptions = (apiHost: string): Partial<PostHogConfig> => ({
  api_host: apiHost,
  defaults: '2026-01-30',
  autocapture: false,
  capture_pageview: 'history_change',
  capture_pageleave: 'if_capture_pageview',
  capture_performance: false,
  capture_heatmaps: false,
  capture_dead_clicks: false,
  rageclick: false,
  disable_surveys: true,
  capture_exceptions: EXCEPTION_CAPTURE_OPTIONS,
  before_send: dropIgnoredExceptions,
  person_profiles: 'identified_only',
  disable_session_recording: !SESSION_REPLAY_ENABLED,
  session_recording: SESSION_RECORDING_OPTIONS,
})
